/**
 * 新着を差し込んで、新しい投稿まで戻る動き (2026-09-21 オーナー指示
 * 「新しい投稿までのスクロールのアニメーションも本格的にしてください」)。
 *
 * 新着ピル (new-posts-pill.ts) と 引っ張って更新 (pull_refresh.ts) の両方から使う。
 *
 *   ①差し込む  — いま見ている投稿の位置を 1px も動かさずに、上へ新着を入れる
 *                (Safari にはスクロールアンカーが無く、Chrome のアンカーは動き方が読めない
 *                 ので、両方とも止めて自分で埋め合わせる)
 *   ②戻る      — 独自の速度曲線で上まで滑る。遠いときは途中まで一気に寄せ、最後の
 *                1.5 画面ぶんだけ滑らせる (見えない所を長々と流さない)
 *   ③現れる    — 新しい投稿が画面に入りはじめたところで、上から順に少しずつ遅れて滑り込む。そのあと左の細い光の筋と
 *                淡い色がしばらく残り、どこまでが新着かが分かる
 *
 * ⚠ opacity も filter も使わない (theme-light の全称 opacity:1 !important と、投稿カードの
 *   filter:none !important に潰される) — clip-path (上から開く幕) と transform で表す (v2.css §255)。
 * ⚠ html { scroll-behavior: smooth } があるので、1 コマずつの scrollTo の前に
 *   scroll-behavior を auto に戻す (戻さないと毎コマに別の滑らかさが重なって震える)。
 * ⚠ rAF だけに頼らない。裏タブや省電力で rAF が止まっても、時間切れで最後の位置へ着地させる
 *   ([[headless-virtual-time-raf-trap]])。
 * ⚠ 指やホイールで触ったら、そこで動きを止めて利用者に返す (取り合わない)。
 * ⚠ 「動きを減らす」設定の端末では、滑らせずに一度で移り、現れる動きも省く。
 */

const reduceMotion = (): boolean => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (_) {
    return false;
  }
};

/** scroll-behavior と scroll anchoring を一時的に止めて fn を実行する。
 *  (paidview.ts の殻の差し替えでも使う: 画面の上に掛かったカードが伸びても下の内容を動かさない) */
export function instant(fn: () => void): void {
  const root = document.documentElement;
  const prevBehavior = root.style.scrollBehavior;
  const prevAnchor = root.style.getPropertyValue("overflow-anchor");
  root.style.scrollBehavior = "auto";
  root.style.setProperty("overflow-anchor", "none");
  try {
    fn();
  } finally {
    root.style.scrollBehavior = prevBehavior;
    if (prevAnchor) root.style.setProperty("overflow-anchor", prevAnchor);
    else root.style.removeProperty("overflow-anchor");
  }
}

/** ① before の直前へ nodes を入れる。画面に見えているものは 1px も動かさない。 */
export function insertKeepingPlace(parent: Node, before: HTMLElement, nodes: Element[]): void {
  instant(() => {
    const top0 = before.getBoundingClientRect().top;
    nodes.forEach((n) => parent.insertBefore(n, before));
    const delta = before.getBoundingClientRect().top - top0;
    if (Math.abs(delta) > 0.5) window.scrollTo(0, window.scrollY + delta);
  });
}

const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOutQuint = (t: number): number => 1 - Math.pow(1 - t, 5);

/**
 * ② targetY まで滑らせる。onProgress(0..1) で進み具合を知らせ、着いたら (または止められたら) done。
 */
export function glideTo(targetY: number, onProgress: (p: number) => void, done: () => void): void {
  const startY = window.scrollY;
  if (reduceMotion() || Math.abs(startY - targetY) < 2) {
    instant(() => window.scrollTo(0, targetY));
    onProgress(1);
    done();
    return;
  }
  const vh = window.innerHeight || 800;
  let from = startY;
  let ease = easeInOutCubic;
  // 遠いとき (3 画面より先) は、見えない所を長々と流さない。最後の 1.5 画面の手前まで
  // 一度で寄せ、そこから減速だけの曲線で滑らせる (動き出しの速さがそのまま続いて見える)。
  if (from - targetY > vh * 3) {
    from = targetY + vh * 1.5;
    instant(() => window.scrollTo(0, from));
    ease = easeOutQuint;
  }
  const dist = from - targetY;
  const dur = Math.min(900, Math.max(460, 320 + dist * 0.3));

  let t0 = 0;
  let over = false;
  const finish = (): void => {
    if (over) return;
    over = true;
    window.removeEventListener("wheel", stop, true);
    window.removeEventListener("touchstart", stop, true);
    window.removeEventListener("keydown", stop, true);
    onProgress(1);
    done();
  };
  // 利用者が触ったらそこで止める (位置はそのまま)。
  const stop = (): void => finish();
  window.addEventListener("wheel", stop, { capture: true, passive: true });
  window.addEventListener("touchstart", stop, { capture: true, passive: true });
  window.addEventListener("keydown", stop, true);

  const frame = (now: number): void => {
    if (over) return;
    if (!t0) t0 = now;
    const p = Math.min(1, (now - t0) / dur);
    instant(() => window.scrollTo(0, from - dist * ease(p)));
    onProgress(p);
    if (p < 1) requestAnimationFrame(frame);
    else finish();
  };
  requestAnimationFrame(frame);
  // rAF が止まっている (裏タブ等) ときの保険: 時間が来たら着地させる。
  window.setTimeout(() => {
    if (over) return;
    instant(() => window.scrollTo(0, targetY));
    finish();
  }, dur + 400);
}

let npGen = 0;

/**
 * ③ 新しい投稿を上から順に現す。prepare で「まだ見えていない」形にしておき、
 * reveal で動かす (滑っている途中から現れ始めるように、2 段に分けてある)。
 *
 * ⚠ 読むと払う殻は、画面に入った瞬間に paidview.ts が本文つきの要素へ**丸ごと差し替える**。
 * 差し替えで印が外れると光の筋が消えるので、paidview.ts の swap が data-np と印を
 * 新しい要素へ引き継ぐ (幕の動きは済んだ扱いの .np-carry)。片付けも要素の参照ではなく
 * data-np で探す (差し替えられた古い要素を掴んでいても意味が無いため)。
 */
export function prepareNew(nodes: Element[]): HTMLElement[] {
  const els = nodes.filter((n): n is HTMLElement => n instanceof HTMLElement);
  if (reduceMotion()) return els;
  const gen = String(++npGen);
  els.forEach((el, i) => {
    el.classList.add("np-new");
    el.dataset.np = gen;
    el.style.setProperty("--np-i", String(Math.min(i, 8)));
  });
  return els;
}

export function revealNew(els: HTMLElement[]): void {
  if (reduceMotion() || els.length === 0) return;
  const gen = els[0].dataset.np || "";
  els.forEach((el) => el.classList.add("np-in"));
  // 光の筋と色が消えたら印を外す (transform / clip-path を残すと、中の固定表示の
  // メニューの基準が変わるため)。いちばん遅いもの + 余裕で片付ける。
  window.setTimeout(() => {
    document.querySelectorAll<HTMLElement>(`[data-np="${gen}"]`).forEach((el) => {
      el.classList.remove("np-new", "np-in", "np-carry");
      el.style.removeProperty("--np-i");
      delete el.dataset.np;
    });
  }, 3400);
}

/**
 * まとめて: 差し込み → (遠ければ) 上まで滑る → 途中から新しい投稿が現れる。
 * targetY は新しい投稿がいちばん上に来る位置 (0 = ページの先頭)。
 */
export function landNewPosts(parent: Node, before: HTMLElement, nodes: Element[], targetY = 0): void {
  const els = prepareNew(nodes);
  insertKeepingPlace(parent, before, els);
  // 新しい投稿のまとまりの下端 (ページの上からの位置)。滑っている途中でここが画面の上端より
  // 下に来た = 新しい投稿が画面に入りはじめた瞬間に現しはじめる。
  // ⚠ 進み具合 (p) で決めない — 遅く現すと、幕が閉じたままの新しい投稿だけが画面に入り、
  //   一瞬まっさらな画面になる (実測)。
  const last = els[els.length - 1];
  const blockBottom = last ? last.getBoundingClientRect().bottom + window.scrollY : 0;
  let shown = false;
  const show = (): void => {
    if (shown) return;
    shown = true;
    revealNew(els);
  };
  if (window.scrollY < blockBottom) show(); // もう見えている (上のほうで押した)
  glideTo(
    targetY,
    () => {
      if (window.scrollY < blockBottom) show();
    },
    show,
  );
}
